// use-auth-status.ts
// This file provides the useUpdateAuthStatusBulk hook for updating authorization status in bulk for multiple cases.
// Integrates with user context and React Query for state management.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UpdateAuthStatusBulkArgs } from "@/types/procedure";
import { useAppState } from "@/contexts/app-state";
import { updateBulkAuthStatus } from "@/services/caseDetails";

/**
 * useUpdateAuthStatusBulk Hook
 * Provides a mutation for updating authorization status in bulk for multiple cases.
 *
 * @returns An object with mutation state, data, and an updateStatusBulk function
 *
 * @example
 * const { updateStatusBulk, isPending } = useUpdateAuthStatusBulk();
 * updateStatusBulk({ ... });
 */
export const useUpdateAuthStatusBulk = () => {
  const queryClient = useQueryClient();
  const { loggedInUser } = useAppState();
  const { data, isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: updateBulkAuthStatus,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["orders"],
      });
    },
  });

  const updateStatusBulk = (body: UpdateAuthStatusBulkArgs["body"]) => {
    mutate({
      user: loggedInUser,
      requestorId: loggedInUser,
      requestorName: loggedInUser,
      requestorRole: loggedInUser,
      body
    });
  };

  return {
    data,
    isPending,
    isError,
    error,
    isSuccess,
    updateStatusBulk,
  };
};
